import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://elementarycargo.com';

/**
 * SEO Component for Elementary Cargo
 * Provides dynamic meta tags, Open Graph, Twitter Cards, hreflang, and structured data
 */
const SEO = ({ 
  titleKey,
  descriptionKey,
  title,
  description,
  path,
  type = 'website',
  imageUrl,
  noIndex = false,
  structuredData,
  breadcrumbs
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentLang = i18n.language || 'en';
  
  // Use translation keys or direct values
  const pageTitle = title || (titleKey ? t(titleKey) : 'Elementary Cargo');
  const pageDescription = description || (descriptionKey ? t(descriptionKey) : t('seo.defaultDescription'));
  const currentPath = path || location.pathname;
  const canonicalUrl = `${BASE_URL}${currentPath}`;
  const ogImage = imageUrl || `${BASE_URL}/og-image.png`;
  
  // Full title with brand
  const fullTitle = pageTitle.includes('Elementary Cargo') 
    ? pageTitle 
    : `${pageTitle} | Elementary Cargo`;

  // Open Graph locale mapping for all supported languages
  // Note: hreflang alternates are intentionally NOT emitted. Valid hreflang requires
  // a distinct crawlable URL per language, which the current routing does not provide.
  const ogLocales = { en: 'en_GB', nl: 'nl_NL', es: 'es_ES', pt: 'pt_BR' };
  const baseLang = (currentLang || 'en').split('-')[0];
  const ogLocale = ogLocales[baseLang] || 'en_GB';
  const ogLocaleAlternates = Object.entries(ogLocales)
    .filter(([lang]) => lang !== baseLang)
    .map(([, locale]) => locale);

  // Default Organization structured data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Elementary Cargo B.V.",
    "legalName": "Elementary Cargo B.V.",
    "description": "Professional logistics coordination services based in the Netherlands. Air, ocean, and road freight coordination through selected partners.",
    "url": BASE_URL,
    "logo": `${BASE_URL}/logo.png`,
    "email": "info@elementarycargo.com",
    "telephone": "+31625381824",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "NL",
      "addressRegion": "Netherlands"
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 52.3676,
        "longitude": 4.9041
      },
      "geoRadius": "5000"
    },
    "sameAs": [
      "https://www.linkedin.com/company/elementary-cargo"
    ],
    "foundingDate": "2020",
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "value": "2-10"
    },
    "vatID": "NL004527618B01",
    "taxID": "42041124"
  };

  // LocalBusiness schema for better local SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${BASE_URL}/#localbusiness`,
    "name": "Elementary Cargo B.V.",
    "description": "Logistics coordination services - air, ocean, road freight, customs, and warehousing",
    "url": BASE_URL,
    "telephone": "+31625381824",
    "email": "info@elementarycargo.com",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "NL"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 52.3676,
      "longitude": 4.9041
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "17:00"
    },
    "priceRange": "$$"
  };

  // Breadcrumb schema
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${BASE_URL}${item.path}`
    }))
  } : null;

  // Combine all structured data
  const allStructuredData = [
    organizationSchema,
    localBusinessSchema,
    ...(structuredData ? [structuredData] : []),
    ...(breadcrumbSchema ? [breadcrumbSchema] : [])
  ];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="author" content="Elementary Cargo B.V." />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Elementary Cargo" />
      <meta property="og:locale" content={ogLocale} />
      {ogLocaleAlternates.map((locale) => (
        <meta key={locale} property="og:locale:alternate" content={locale} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {allStructuredData.map((data) => (
        <script key={`schema-${data['@type']}`} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
